import { useState } from "react";
import { useLocation } from "wouter";
import { apiClient, ApiError } from "../api/client.js";

// Vaihe 4.B.3.UM.4 — accept-invite-sivu.
// Avautuu invite-URL:lla: /tervetuloa?token=...
// Käyttäjä asettaa nimen + salasanan → tili luodaan + redirect /projektit:iin.

export function Tervetuloa() {
  const [, navigate] = useLocation();

  // Token URL-parametrista (ei pidetä state:ssa koska ei haluta että UI
  // näyttää sen — turvallisuus-mielessä token ei ole rendattu DOM:iin)
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <section className="mx-auto max-w-md px-6 py-16 text-center">
        <h2 className="text-2xl font-semibold text-slate-100">Kutsu puuttuu</h2>
        <p className="mt-4 text-slate-400">
          Avaa tämä sivu sähköpostissa saamasi linkin kautta. Linkki sisältää
          tarvittavan tokenin.
        </p>
      </section>
    );
  }

  function validate(): string | null {
    if (name.trim().length === 0) return "Anna nimi";
    if (password.length < 8) return "Salasanan pitää olla vähintään 8 merkkiä";
    if (password !== passwordConfirm) return "Salasanat eivät täsmää";
    return null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v !== null) {
      setError(v);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.acceptInvite(token, password, name.trim());
      // Onnistui — better-auth asetti session-cookien → navigoi koti-sivulle
      navigate("/projektit");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) setError("Kutsua ei löytynyt — linkki on virheellinen");
        else if (err.status === 410) setError("Kutsu on jo käytetty tai vanhentunut");
        else setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-6 py-12">
      <header className="text-center">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-100">
          Tervetuloa Krattiin
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          Aseta tilillesi nimi ja salasana — voit kirjautua tämän jälkeen sisään
          milloin tahansa email-osoitteellasi.
        </p>
      </header>

      <form
        onSubmit={submit}
        className="mt-8 space-y-4 rounded-lg border border-slate-700 bg-slate-900 p-6"
      >
        <label className="block">
          <span className="text-sm font-medium text-slate-300">Nimi</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            disabled={submitting}
            className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
            placeholder="Etunimi Sukunimi"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-300">Salasana</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={submitting}
            className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
            placeholder="Vähintään 8 merkkiä"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-300">Salasana uudelleen</span>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            disabled={submitting}
            className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
          />
        </label>

        {error && (
          <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
        >
          {submitting ? "Luodaan tiliä…" : "Luo tili ja kirjaudu sisään"}
        </button>
      </form>
    </section>
  );
}
